<template>
  <!--
    The PlatformFooter (EOCube Design System: components/PlatformFooter): the catalog is a public content surface,
    so it carries the full footer like the homepage and the docs: the consortium's partner strip, the funding
    sentence with the funders' marks and the disclaimer, the legal links and the copyright line with the version.
    Partners and funders whose files are not in the design system yet show a dashed eo-pending plate. The funding
    sentence, the disclaimer and the copyright line are the platform copy (the design system's copy.json, which eo.js
    carries as EOCube.copy): never retyped here.
  -->
  <footer class="eo eo-footer">
    <WidgetHook id="footer-start" />
    <div class="eo-footer-inner">
      <div class="eo-partners" role="group" :aria-label="$t('eocube.footer.partners')">
        <template v-for="partner in partners" :key="partner.name">
          <a v-if="partner.logo" class="eo-partner" :href="partner.url" rel="noopener" :title="partner.name">
            <img :src="partner.logo" :alt="partner.name" height="28" loading="lazy">
          </a>
          <span v-else class="eo-partner eo-pending" :title="partner.name">{{ partner.name }}</span>
        </template>
      </div>
      <div class="eo-footer-row">
        <div class="eo-funding-block">
          <!-- eslint-disable-next-line vue/no-v-html -- the design system's own markup (copy.funding_html), no data -->
          <p class="eo-funding" v-html="copy.funding_html[copyLanguage]" />
          <div class="eo-funders" role="group" :aria-label="$t('eocube.footer.funders')">
            <span class="eo-funder eo-pending">{{ $t('eocube.footer.government') }}</span>
            <span class="eo-funder eo-pending">UEFISCDI</span>
          </div>
          <p class="eo-disclaimer">{{ copy.disclaimer[copyLanguage] }}</p>
        </div>
        <div class="eo-footer-meta">
          <nav class="eo-footer-links" :aria-label="$t('eocube.footer.legal')">
            <a v-for="link in configuredLinks" :key="link.url" :href="link.url" rel="noopener">{{ link.label }}</a>
            <a :href="links.terms">{{ $t('eocube.footer.terms') }}</a>
            <a :href="links.privacy">{{ $t('eocube.footer.privacy') }}</a>
            <a :href="links.gitlab" rel="noopener">GitLab</a>
          </nav>
          <span>
            {{ copyright }} ·
            <a class="eo-code-sm" href="https://github.com/radiantearth/stac-browser" rel="noopener" :title="versionTitle">STAC Browser {{ browserVersion }}</a>
          </span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapState } from 'vuex';
import { PLATFORM } from './platform';
import rev from './vendor/REV?raw';
import rocs from './vendor/logos/partners/rocs.svg';
import uvt from './vendor/logos/partners/uvt.png';
import upb from './vendor/logos/partners/upb.svg';
import rosa from './vendor/logos/partners/rosa.png';
import beia from './vendor/logos/partners/beia.svg';

// The consortium, in the design system's order (PlatformFooter); ANM's file is not in rocs/branding yet.
const PARTNERS = [
  { name: 'ROCS', url: 'https://rocs.uvt.ro', logo: rocs },
  { name: 'UVT', url: 'https://www.uvt.ro', logo: uvt },
  { name: 'UPB', url: 'https://upb.ro', logo: upb },
  { name: 'ANM', url: 'https://www.meteoromania.ro', logo: null },
  { name: 'ROSA', url: 'https://www.rosa.ro', logo: rosa },
  { name: 'BEIA', url: 'https://www.beia.ro', logo: beia }
];

const DESIGN_SYSTEM = (/^tag=(.+)$/m.exec(rev) || [])[1];
// the platform copy (system/copy.json), in eo.js since the design system's v0.4.0: Romanian and English only
const COPY = window.EOCube.copy;

export default {
  name: 'EocubeFooter',
  data() {
    return {
      partners: PARTNERS,
      copy: COPY,
      copyright: window.EOCube.copyright()
    };
  },
  computed: {
    ...mapState(['footerLinks', 'locale']),
    links() {
      return PLATFORM.links(this.locale);
    },
    copyLanguage() {
      return this.locale === 'ro' ? 'ro' : 'en';
    },
    // STAC Browser's own footerLinks option still works: they come first in the links row.
    configuredLinks() {
      if (!Array.isArray(this.footerLinks)) {
        return [];
      }
      return this.footerLinks.map(link => ({
        url: link.url,
        label: this.$te(`footerLinks.${link.label}`) ? this.$t(`footerLinks.${link.label}`) : link.label
      }));
    },
    browserVersion() {
      return typeof STAC_BROWSER_VERSION !== 'undefined' ? STAC_BROWSER_VERSION : '';
    },
    versionTitle() {
      return DESIGN_SYSTEM && DESIGN_SYSTEM !== 'untagged' ? `EOCube Design System ${DESIGN_SYSTEM}` : 'EOCube Design System';
    }
  }
};
</script>
